import React, {useEffect, useState} from 'react'
import {useParams} from 'react-router-dom'
import {createQueryFn, useSolv, useTenant} from "../components/SolvProvider"
import {useIntlEx} from "../components/IntlUtils"
import {
  ActionButton,
  DropdownMenu,
  DropdownMenuItem,
  FormBody,
  FormGroup,
  FormGroups,
  FormHeader,
  MainContainer
} from "../components/FormComps.js"
import MainMenu from "../MainMenu"
import {openDialogCurried} from "../components/DialogUtils";
import SignupRequestApproveDialog from "./SignupRequestApproveDialog";
import {getSelfSignupFieldOptionFromValue, selfSignupEtcFieldDefs} from "../components/SigninupUtils";
import {isNonEmptyObject} from "../components/Utils";
import {StatusTagLabel} from "../components/TagBox";
import SignupRequestDeclineDialog from "./SignupRequestDeclineDialog";
import getLogger from "../components/Logging.js"
import '../App.css'
import {useQuery} from "react-query";
import {TenantLink} from "../components/AccountUtils";

const log = getLogger("SignupRequest")

export default function SignupRequest(props) {

  const {env, api, session: {user}, setFatal, setBusy} = useSolv()
  const {intl } = useIntlEx()

  const params = useParams()

  const {tenant} = useTenant(params.tenantId)

  const [alert, setAlert] = useState(null)

  const [etcRequest, setEtcRequest] = useState({})
  const [etcResolve, setEtcResolve] = useState(null)

  const {data: signupRequest, isLoading: signupRequestLoading} = useQuery({
    queryFn: createQueryFn(api.getSignupRequest, params.signupRequestId),
    queryKey: ["signupRequest", params.signupRequestId],
    onError: (error) => setFatal(error),
    enabled: !!params.signupRequestId
  })

  const {data: country, isLoading: countryLoading} = useQuery({
    queryFn: createQueryFn(api.getCountry, signupRequest?.countryCode),
    queryKey: ["country", signupRequest?.countryCode],
    onError: (error) => setFatal(error),
    enabled: !!signupRequest
  })

  const {data: invitedTenant, isLoading: invitedTenantLoading} = useQuery({
    queryFn: createQueryFn(api.getTenantInfo, signupRequest?.invitedTenantId),
    queryKey: ["tenantInfo", signupRequest?.invitedTenantId],
    onError: (error) => setFatal(error),
    enabled: !!signupRequest?.invitedTenantId
  })


  useEffect(() => {
    if (signupRequestLoading) {
      setBusy(intl.msg("loading"))
    }
    else {
      setBusy(null)
    }
  }, [signupRequestLoading])

  useEffect(() => {
    log.debug(">>>Z: signupRequest: data=", signupRequest)
    if (signupRequest != null && isNonEmptyObject(signupRequest.etc)) {
      if (signupRequest.etc.request) {
        setEtcRequest(signupRequest.etc.request)
      }
      if (signupRequest.etc.resolve) {
        setEtcResolve(signupRequest.etc.resolve)
      }
    }
  }, [signupRequest])


  function canApprove() {
    return ("PENDING" === signupRequest?.status) && user.isSystem() && user.isManagerOrAbove()
  }

  function canDecline() {
    return ("PENDING" === signupRequest?.status) && user.isSystem() && user.isManagerOrAbove()
  }

  function canReinstate() {
    return ("DECLINED" === signupRequest?.status) && user.isSystem() && user.isManagerOrAbove()
  }

  function FormControls() {

    const ctls = []

    if (tenant && signupRequest) {

      if (canApprove()) {
        ctls.push(
          <ActionButton name="approve" flavor="primary" onClick={openDialogCurried("dlg_signup_request_approve")}/>
        )
      }
      if (canDecline()) {
        ctls.push(
          <ActionButton name="decline" flavor="secondary" onClick={openDialogCurried("dlg_signup_request_decline")}/>
        )
      }

      ctls.push(
        <MoreMenu/>
      )

    }
    return (ctls)
  }

  function MoreMenu(props) {
    const ctls = []

    if (tenant && signupRequest) {

      if (canReinstate()) {
        ctls.push(
          <DropdownMenuItem msg="reinstate" icon="approve" onClick={openDialogCurried("dlg_signup_request_approve", {op: "reinstate"})}/>
        )
      }
    }

    return (
      ctls.length > 0 &&
        <DropdownMenu>
          {ctls}
        </DropdownMenu>
    )
  }

  // useEffect(() => {
  //   log.debug(">>>Z: signupRequest=", signupRequest)
  // }, [signupRequest])

  return (
    tenant &&
      <>
        <MainContainer tenant={tenant} menu={MainMenu} allowIfAccessingAs={"*/*/SYSTEM"}>

          <FormHeader>
            <FormHeader.Toolbar>
              <FormHeader.Toolbar.Title>
                {intl.msg("signup_request")} {" - " + (signupRequest?.displayName || "...")}
              </FormHeader.Toolbar.Title>
              <FormHeader.Toolbar.Controls>
                <FormControls/>
              </FormHeader.Toolbar.Controls>
            </FormHeader.Toolbar>
            <FormHeader.Alert alert={alert}/>
          </FormHeader>

          <FormBody>

            <FormGroup>
              <div className="d-flex flex-column justify-content-center align-items-center gap-3 w-100">
                <StatusTagLabel status={signupRequest?.status || "..."} size="xl"/>
              </div>
            </FormGroup>

            {
              invitedTenant?.tenant ? (
                <>
                  <FormGroup>
                    <FormGroup.Label text={intl.msg("signup_request_invited_account")}/>
                    <FormGroup.Controls>
                      <TenantLink tenant={invitedTenant.tenant}/>
                    </FormGroup.Controls>
                  </FormGroup>
                </>
              ) : etcResolve?.message ? (
                <>
                  <FormGroup>
                    <FormGroup.Label text={intl.msg("signup_request_decline_message")}/>
                    <FormGroup.Controls>
                      {etcResolve.message}
                    </FormGroup.Controls>
                  </FormGroup>
                </>
              ) : (
                <></>
              )
            }

            <FormGroups>

              <FormGroup>
                <FormGroup.Label text={intl.msg("signup_request_full_name")}/>
                <FormGroup.Controls>
                  {signupRequest?.fullName || "..."}
                </FormGroup.Controls>
              </FormGroup>

              <FormGroup>
                <FormGroup.Label text={intl.msg("signup_request_email_address")}/>
                <FormGroup.Controls>
                  {signupRequest?.emailAddress || "..."}
                </FormGroup.Controls>
              </FormGroup>

              <FormGroup>
                <FormGroup.Label text={intl.msg("signup_request_country")}/>
                <FormGroup.Controls>
                  {country?.displayName || "..."}
                </FormGroup.Controls>
              </FormGroup>

            </FormGroups>

            <FormGroups hidden={!isNonEmptyObject(etcRequest)}>
              {
                Object.entries(etcRequest).map(([key, value]) => {
                  const fn = key.toLowerCase()
                  const def = selfSignupEtcFieldDefs[key]
                  return (
                    <FormGroup>
                      <FormGroup.Label text={intl.msg(`signup_request_${fn}`, {brand: env.displayName})}/>
                      <FormGroup.Controls>
                        {
                          ["SELECT"].includes(def?.type) ? (
                            getSelfSignupFieldOptionFromValue(key, value)?.label
                          ) : (
                            value
                          )
                        }
                      </FormGroup.Controls>
                    </FormGroup>
                  )
                })
              }
            </FormGroups>

          </FormBody>
        </MainContainer>

        <SignupRequestApproveDialog
          signupRequestId={params.signupRequestId}
        />

        <SignupRequestDeclineDialog
          signupRequestId={params.signupRequestId}
        />

      </>

  )
}
